"use client";

import { createContext, useContext } from 'react';

/**
 * Creates a context with a custom hook and proper type checking
 * 
 * @example
 * // Create a context
 * const [UserProvider, useUser] = createCtx<UserContextType>();
 * 
 * // Use in component
 * const App = () => (
 *   <UserProvider value={{ name: 'John' }}>
 *     <Profile />
 *   </UserProvider>
 * );
 * 
 * // Access in any child component
 * const Profile = () => {
 *   const { name } = useUser();
 *   return <div>{name}</div>;
 * };
 */
export function createCtx<T>() {
  // Create context with undefined as default - this forces consumers to use the Provider
  const ctx = createContext<T | undefined>(undefined);
  
  // Create a custom hook with type checking and better error message
  function useCtx(componentName = 'component') {
    const c = useContext(ctx);
    if (c === undefined) {
      throw new Error(
        `use${ctx.displayName || 'Context'} must be used within a ${ctx.displayName || 'Context'}Provider. Check the ${componentName} component.`
      );
    }
    return c;
  }
  
  // Return the Provider and custom hook
  return [ctx.Provider, useCtx] as const;
}

/**
 * Creates a context with default value and a custom hook
 * 
 * @example
 * // Create a context with default value
 * const [ThemeProvider, useTheme] = createCtxWithDefault({ mode: 'light' });
 */
export function createCtxWithDefault<T>(defaultValue: T) {
  const ctx = createContext<T>(defaultValue);
  
  function useCtx() {
    return useContext(ctx);
  }
  
  return [ctx.Provider, useCtx] as const;
} 